import React from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import {
  FaArrowLeft, FaComments, FaCogs, FaRocket, FaHandshake,
  FaChartLine, FaShieldAlt, FaUsers, FaCheckCircle,
} from 'react-icons/fa';
import PageTransition from '../components/PageTransition';
import { fadeInUp, staggerContainer, staggerContainerSlow, scaleIn, viewport } from '../utils/animations';

const steps = [
  {
    icon: FaComments,
    step: '01',
    title: 'Discovery Call',
    desc: 'We kick off with a no-obligation discovery call to understand your goals, challenges, and vision. We ask the right questions to make sure we\'re the right fit, and that we fully understand the problem before proposing a solution.',
  },
  {
    icon: FaCogs,
    step: '02',
    title: 'Scoping & Proposal',
    desc: 'Based on our discovery, we craft a detailed proposal covering scope, timeline, tech stack, team composition, and transparent pricing. No hidden fees, no vague estimates, just clear deliverables and milestones.',
  },
  {
    icon: FaHandshake,
    step: '03',
    title: 'Onboarding & Kickoff',
    desc: 'Once the SOW is signed and the deposit is received, we kick off within days. You\'ll meet your dedicated team, get access to our project management tools, and align on communication rhythms and sprint cadence.',
  },
  {
    icon: FaRocket,
    step: '04',
    title: 'Agile Delivery',
    desc: 'We work in 1-2 week sprints with weekly demos so you see progress constantly. Our engineers, designers, and QA work in lockstep. Feedback loops are short, decisions are fast, and quality is non-negotiable.',
  },
  {
    icon: FaChartLine,
    step: '05',
    title: 'Review & Iteration',
    desc: 'At the end of each sprint, we review what was built, gather your feedback, and refine priorities for the next cycle. You always know what\'s coming next, no black boxes, no surprises.',
  },
  {
    icon: FaCheckCircle,
    step: '06',
    title: 'Launch & Handover',
    desc: 'When we\'re ready to ship, we handle deployment, testing, and go-live support. After launch, we provide full documentation, knowledge transfer, and optional retainer support to keep things running smoothly.',
  },
];

const principles = [
  {
    icon: FaShieldAlt,
    title: 'Senior-Only Teams',
    desc: 'Every project is staffed with senior engineers and designers. We don\'t offshore or hand work to juniors, the people you meet are the people who build.',
  },
  {
    icon: FaComments,
    title: 'Radical Transparency',
    desc: 'You\'ll always know where things stand. We share progress, blockers, and decisions openly and honestly, even when it\'s not the news you hoped for.',
  },
  {
    icon: FaUsers,
    title: 'We Work as Your Team',
    desc: 'We embed directly into your workflow, Slack, and tools. We operate as an extension of your team, not a black-box vendor.',
  },
  {
    icon: FaChartLine,
    title: 'Outcome-Focused',
    desc: 'We\'re not here to log billable hours, we\'re here to help you achieve real outcomes. We measure success by what we ship and the value it delivers.',
  },
];

const HowWeOperate = () => (
  <PageTransition>
    <div className="bg-transparent min-h-screen">

      {/* ── Hero ─────────────────────────────────────────────── */}
      <section className="relative pt-32 pb-20 overflow-hidden">
        <div className="absolute inset-0 grid-pattern opacity-30 pointer-events-none" />
        <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[500px] h-[400px] rounded-full bg-gray-700/10 blur-3xl pointer-events-none" />

        <div className="container mx-auto px-4 sm:px-6 lg:px-8 relative z-10 max-w-4xl">
          <motion.div variants={fadeInUp} initial="hidden" animate="visible" className="mb-8">
            <Link to="/" className="inline-flex items-center gap-2 text-gray-400 hover:text-white text-sm font-semibold transition-colors duration-200 group">
              <FaArrowLeft className="text-xs group-hover:-translate-x-0.5 transition-transform duration-200" />
              Back to Home
            </Link>
          </motion.div>

          <motion.div variants={staggerContainer} initial="hidden" animate="visible" className="text-center">
            <motion.div variants={fadeInUp} className="inline-flex mb-6">
              <div className="flex items-center gap-2 px-4 py-2 bg-gray-900/60 border border-gray-700 rounded-full">
                <FaCogs className="text-gray-400 text-xs" />
                <span className="text-xs font-semibold tracking-widest text-gray-300 uppercase">Process</span>
              </div>
            </motion.div>
            <motion.h1 variants={fadeInUp} className="text-4xl md:text-5xl lg:text-6xl font-black text-white mb-6 leading-tight">
              How We <span className="animated-gradient-text">Operate</span>
            </motion.h1>
            <motion.p variants={fadeInUp} className="text-gray-400 text-lg max-w-2xl mx-auto">
              We believe great software is built on trust, transparency, and tight collaboration.
              Here's exactly how we work with clients, from the first call to launch day.
            </motion.p>
          </motion.div>
        </div>
      </section>

      {/* ── Process Steps ─────────────────────────────────────── */}
      <section className="relative py-20 overflow-hidden">
        <div className="absolute inset-0 dot-pattern opacity-20 pointer-events-none" />
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 relative z-10 max-w-5xl">

          <div className="space-y-6">
            {steps.map((step, i) => (
              <motion.div
                key={i}
                variants={fadeInUp}
                initial="hidden"
                whileInView="visible"
                viewport={viewport}
                className="glass-card rounded-2xl p-8 flex flex-col sm:flex-row items-start gap-6 group card-hover-lift"
              >
                {/* Step number + icon */}
                <div className="flex items-center gap-4 shrink-0">
                  <span className="text-4xl font-black text-gray-700 w-10 text-right leading-none">{step.step}</span>
                  <div className="w-12 h-12 rounded-xl bg-gray-800 border border-gray-700 flex items-center justify-center shrink-0 group-hover:border-gray-500 transition-colors duration-300">
                    <step.icon className="text-gray-300 text-lg" />
                  </div>
                </div>
                {/* Content */}
                <div>
                  <h3 className="text-white font-black text-xl mb-2">{step.title}</h3>
                  <p className="text-gray-400 leading-relaxed text-sm">{step.desc}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ── Our Principles ────────────────────────────────────── */}
      <section className="relative py-20 overflow-hidden">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-5xl">
          <motion.div
            variants={staggerContainer} initial="hidden" whileInView="visible" viewport={viewport}
            className="text-center mb-14"
          >
            <motion.h2 variants={fadeInUp} className="text-3xl md:text-4xl font-black text-white mb-4">
              What Makes Us <span className="animated-gradient-text">Different</span>
            </motion.h2>
            <motion.p variants={fadeInUp} className="text-gray-400 max-w-xl mx-auto text-sm">
              It's not just what we build, it's how we show up every day.
            </motion.p>
          </motion.div>

          <motion.div
            variants={staggerContainerSlow} initial="hidden" whileInView="visible" viewport={viewport}
            className="grid grid-cols-1 md:grid-cols-2 gap-6"
          >
            {principles.map((p, i) => (
              <motion.div
                key={i}
                variants={scaleIn}
                whileHover={{ y: -4, transition: { duration: 0.2 } }}
                className="glass-card rounded-2xl p-8 group card-hover-lift shine-hover"
              >
                <div className="w-12 h-12 rounded-xl bg-gray-800 border border-gray-700 flex items-center justify-center mb-5 group-hover:border-gray-500 transition-colors duration-300">
                  <p.icon className="text-gray-300 text-lg" />
                </div>
                <h3 className="text-white font-bold text-lg mb-3">{p.title}</h3>
                <p className="text-gray-400 text-sm leading-relaxed">{p.desc}</p>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* ── CTA ──────────────────────────────────────────────── */}
      <section className="relative py-24 overflow-hidden border-t border-gray-800/60">
        <div className="spotlight w-96 h-96 top-1/2 left-1/2" />
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 relative z-10 text-center max-w-2xl">
          <motion.div variants={staggerContainer} initial="hidden" whileInView="visible" viewport={viewport}>
            <motion.h2 variants={fadeInUp} className="text-3xl md:text-4xl font-black text-white mb-6">
              Ready to get started?
            </motion.h2>
            <motion.p variants={fadeInUp} className="text-gray-400 mb-10">
              Book a free discovery call and let's talk about what you're building.
            </motion.p>
            <motion.div variants={fadeInUp} className="flex flex-wrap justify-center gap-4">
              <Link
                to="/contact"
                className="group flex items-center gap-2 px-8 py-4 bg-white text-gray-950 font-bold rounded-xl hover:bg-gray-100 transition-all duration-300 hover:-translate-y-1 shine-hover"
              >
                Start a Project
                <FaRocket className="text-sm" />
              </Link>
              <Link
                to="/about"
                className="flex items-center gap-2 px-8 py-4 border border-gray-700 text-gray-300 font-semibold rounded-xl hover:border-gray-500 hover:text-white transition-all duration-300 hover:-translate-y-1"
              >
                Learn About Us
              </Link>
            </motion.div>
          </motion.div>
        </div>
      </section>

    </div>
  </PageTransition>
);

export default HowWeOperate;
